// Materialised onboarding status computed by the owning services and pushed into IAM.
// None of these fields can be assumed present: the gateway ships after the frontend,
// so every consumer must treat an absent/null value as "not assessed", never as complete.

export type OnboardingModuleKey = 'teams' | 'pay' | 'time';

export type OnboardingStatusFilter = 'ready' | 'required_missing' | 'recommended_pending';

export interface OnboardingItem {
  key?: string;
  label?: string;
}

// Everything the per-module status and the user-level roll-up have in common —
// enough to render a progress row or a chip without knowing which one you hold.
export interface OnboardingStatusBase {
  is_complete?: boolean;
  completion_percent?: number;
  missing_required?: OnboardingItem[];
  missing_recommended?: OnboardingItem[];
  // Naive UTC timestamp (no offset) — parse with dayjs.utc()
  evaluated_at?: string | null;
}

export interface ModuleOnboardingStatus extends OnboardingStatusBase {
  // Human-readable module name, e.g. 'Teams' | 'Payroll' | 'Time & Attendance'
  module?: string;
  // Data-point counts behind completion_percent. Present on the per-company breakdown,
  // not guaranteed elsewhere.
  required_total?: number;
  required_filled?: number;
  recommended_total?: number;
  recommended_filled?: number;
}

// Module-keyed status on listing rows and the single-worker endpoint
// (GET /public/v1/companies/{company_id}/workers/{user_id}).
// A null module means "not visible to this caller or not evaluated" — not "not set up".
export interface WorkerOnboarding {
  teams?: ModuleOnboardingStatus | null;
  pay?: ModuleOnboardingStatus | null;
  time?: ModuleOnboardingStatus | null;
}

// Per-company roll-up inside the user-level status: the same fields as the top-level
// roll-up, plus the company identity and the module breakdown for that company.
// trading_name is frequently null — fall back to company_name.
export interface CompanyOnboardingStatus extends OnboardingStatusBase {
  company_id?: string;
  company_name?: string | null;
  trading_name?: string | null;
  evaluated?: boolean;
  by_module?: WorkerOnboarding | null;
}

// Single cross-module roll-up carried by the user endpoints —
// POST /public/v1/users/me/basic and POST /admin/v1/users/{id}/basic,
// both with add_core_data: true. There is no per-module breakdown here.
// evaluated: false means nothing has been computed yet — render neutral, not 0%.
// by_company splits the same roll-up per company the user belongs to, keyed by company id.
export interface UserOnboardingStatus extends OnboardingStatusBase {
  evaluated?: boolean;
  by_company?: Record<string, CompanyOnboardingStatus> | null;
}

export interface OnboardingRecomputeTriggeredItem {
  module?: string;
  // Internal identifier — must never be rendered in the UI
  workflow_id?: string;
  // false = a run for the same target is already in flight; still a success
  started?: boolean;
}

export interface OnboardingRecomputeResponse {
  status?: boolean;
  message?: string;
  triggered?: OnboardingRecomputeTriggeredItem[];
}

// Display labels for the module keys. Do not use the MODULES enum here —
// its TIME value is 'Time&Attendance' (no spaces).
export const ONBOARDING_MODULE_LABELS: Record<OnboardingModuleKey, string> = {
  teams: 'Teams',
  pay: 'Payroll',
  time: 'Time & Attendance',
};
